import { supabase } from "@/integrations/supabase/client";

/**
 * Runtime design-token override system.
 *
 * Mirrors `dbTranslations.ts` strategy:
 *  - Loads design tokens (CSS variable overrides) from `design_tokens` table.
 *  - Caches the merged token set in localStorage keyed by a tiny version probe.
 *  - On boot: hydrates from cache instantly, then probes the DB version and
 *    only re-fetches when something actually changed.
 *  - Injects values into a single dynamic <style id="design-tokens-override">
 *    element so they win over the static :root rules in index.css.
 */

export type TokenType =
  | "color"
  | "typography"
  | "spacing"
  | "gradient"
  | "shadow"
  | "radius"
  | "raw";

export interface DesignToken {
  token_key: string;     // e.g. "primary", "radius", "font-display"
  token_type: TokenType;
  token_value: string;   // e.g. "38 92% 50%", "0.75rem", "linear-gradient(...)"
  category?: string;
  description?: string | null;
}

const CACHE_KEY = "design_tokens_cache_v1";
const STYLE_ID = "design-tokens-override";

interface CachedBundle {
  version: string;       // `${maxUpdatedAt}|${count}`
  cachedAt: number;
  tokens: DesignToken[];
}

function readCache(): CachedBundle | null {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as CachedBundle;
    if (!parsed?.version || !Array.isArray(parsed.tokens)) return null;
    return parsed;
  } catch {
    return null;
  }
}

function writeCache(bundle: CachedBundle) {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify(bundle));
  } catch (e) {
    console.warn("[design-tokens] cache write failed:", e);
  }
}

export function clearDesignTokensCache() {
  try { localStorage.removeItem(CACHE_KEY); } catch { /* ignore */ }
}

/**
 * Build the CSS rule body that overrides :root. Each token is rendered as a
 * `--<token_key>: <value>;` line. The dynamic <style> tag is appended late in
 * <head> so it wins over index.css cascade.
 */
function buildCss(tokens: DesignToken[]): string {
  if (tokens.length === 0) return "";
  const lines = tokens
    .filter((t) => t.token_key && typeof t.token_value === "string" && t.token_value.length > 0)
    .map((t) => `  --${t.token_key}: ${t.token_value};`);
  if (lines.length === 0) return "";
  return `:root {\n${lines.join("\n")}\n}\n`;
}

function applyTokens(tokens: DesignToken[]) {
  if (typeof document === "undefined") return;
  const css = buildCss(tokens);
  let styleEl = document.getElementById(STYLE_ID) as HTMLStyleElement | null;
  if (!styleEl) {
    styleEl = document.createElement("style");
    styleEl.id = STYLE_ID;
    styleEl.setAttribute("data-source", "design-tokens");
    document.head.appendChild(styleEl);
  }
  // Empty string clears overrides (falls back to index.css :root).
  styleEl.textContent = css;
  // Notify listeners (e.g. live admin preview).
  window.dispatchEvent(new CustomEvent("design-tokens:applied", { detail: { count: tokens.length } }));
}

/**
 * Lightweight version probe: max(updated_at) + row count. Avoids fetching the
 * whole token set when nothing changed.
 */
async function fetchVersion(): Promise<string | null> {
  try {
    const [latest, countRes] = await Promise.all([
      supabase
        .from("design_tokens")
        .select("updated_at")
        .order("updated_at", { ascending: false })
        .limit(1)
        .maybeSingle(),
      supabase
        .from("design_tokens")
        .select("*", { count: "exact", head: true }),
    ]);
    if (latest.error) {
      console.warn("[design-tokens] version probe failed:", latest.error.message);
      return null;
    }
    if (countRes.error) {
      console.warn("[design-tokens] count probe failed:", countRes.error.message);
      return null;
    }
    const maxUpdated = latest.data?.updated_at ?? "0";
    const count = countRes.count ?? 0;
    return `${maxUpdated}|${count}`;
  } catch (e) {
    console.warn("[design-tokens] probe error:", e);
    return null;
  }
}

async function fetchAllTokens(): Promise<DesignToken[] | null> {
  const { data, error } = await supabase
    .from("design_tokens")
    .select("token_key, token_type, token_value, category, description")
    .order("category", { ascending: true })
    .order("sort_order", { ascending: true });
  if (error) {
    console.warn("[design-tokens] fetch failed:", error.message);
    return null;
  }
  return (data || []) as DesignToken[];
}

/**
 * Apply DB design-token overrides into the document.
 *
 * Strategy:
 *  1. Hydrate from localStorage cache instantly (no flash).
 *  2. Probe DB version with a tiny query.
 *  3. If version matches cache → done.
 *  4. Otherwise fetch, apply, and update cache.
 *
 * Pass `{ force: true }` after admin edits to bypass the cache.
 */
export async function applyDesignTokenOverrides(opts: { force?: boolean } = {}) {
  const { force = false } = opts;
  const cached = readCache();

  // 1) Instant hydrate.
  if (cached) {
    applyTokens(cached.tokens);
  }

  try {
    const remoteVersion = force ? null : await fetchVersion();
    if (!force && remoteVersion && cached && remoteVersion === cached.version) {
      return;
    }
    const tokens = await fetchAllTokens();
    if (!tokens) return;
    applyTokens(tokens);
    const finalVersion = remoteVersion ?? (await fetchVersion()) ?? `unknown-${Date.now()}`;
    writeCache({ version: finalVersion, cachedAt: Date.now(), tokens });
  } catch (e) {
    console.warn("[design-tokens] applyDesignTokenOverrides error:", e);
  }
}
